import { useEffect, useState, useCallback, useRef } from "react";
import { Star, MessageSquare, Crown, Reply, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { sanitizeHtml } from "../lib/sanitize";
import { PipeText } from "../lib/pipeText";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const fmtD = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export function Stars({ value, size = 15, onPick, testid }) {
  return (
    <span className="inline-flex gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size}
          className={`${n <= Math.round(value || 0) ? "text-amber-400 fill-amber-400" : "text-zinc-600"} ${onPick ? "cursor-pointer hover:scale-110 transition-transform" : ""}`}
          onClick={onPick ? () => onPick(n) : undefined} data-testid={onPick ? `star-pick-${n}` : undefined} />
      ))}
    </span>
  );
}

function ReviewDialog({ entityType, entityId, existing, onClose, onSaved }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [title, setTitle] = useState(existing?.title || "");
  const [body, setBody] = useState(existing?.body || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!rating) { toast.error("Pick a star rating"); return; }
    if (!body.trim()) { toast.error("Write a few words about your experience"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { entity_type: entityType, entity_id: entityId, rating, title, body });
      toast.success("Review submitted — it will appear once approved");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-md" data-testid="review-dialog">
        <DialogHeader><DialogTitle className="font-display">{existing ? "Edit your review" : "Write a review"}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Reviews are checked by our team before going live.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><p className="text-xs text-zinc-400 mb-1.5">Your rating</p><Stars value={rating} size={26} onPick={setRating} testid="review-stars" /></div>
          <input className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm" placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="review-title" />
          <textarea rows={4} className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm" placeholder="Tell everyone about your experience..." value={body} onChange={(e) => setBody(e.target.value)} data-testid="review-body" />
          <button onClick={save} disabled={busy} className="w-full bg-rose-500 hover:bg-rose-600 font-bold py-2.5 rounded-lg text-sm disabled:opacity-40 flex justify-center items-center gap-2" data-testid="review-submit">
            {busy && <Loader2 size={15} className="animate-spin" />} Submit review
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InviteInsiderDialog({ entityType, entityId, onClose }) {
  const [experience, setExperience] = useState("");
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [covered, setCovered] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!experience.trim()) { toast.error("Tell us which experience you'd like us to review"); return; }
    if (!covered) { toast.error("Please confirm you'll cover the experience for the visiting team"); return; }
    setBusy(true);
    try {
      await api.post("/insider-requests", { entity_type: entityType, entity_id: entityId, experience, message, contact, covered: true });
      toast.success("Invitation sent — the VIP team will reach out to schedule a visit");
      onClose();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-md" data-testid="insider-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Crown size={18} className="text-amber-400" /> Invite a VIP Insider Review</DialogTitle>
          <DialogDescription className="text-zinc-400">100% free and editorial. Our team arrives identified with Gulf Coast VIP credentials — you cover the experience itself (the meal, the tickets, the stay), never a fee.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <textarea rows={3} className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm" placeholder="Which experience should we review? (e.g., chef's tasting menu, VIP cabana day, sunset dolphin cruise). This helps us size the team, photographer/videographer, and stay." value={experience} onChange={(e) => setExperience(e.target.value)} data-testid="insider-experience" />
          <textarea rows={2} className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm" placeholder="Anything else? (best nights, signature items, must-sees)..." value={message} onChange={(e) => setMessage(e.target.value)} data-testid="insider-message" />
          <input className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm" placeholder="Best contact (phone or email)" value={contact} onChange={(e) => setContact(e.target.value)} data-testid="insider-contact" />
          <label className="flex items-start gap-2.5 text-xs text-zinc-300 leading-relaxed cursor-pointer rounded-lg border border-amber-400/25 bg-amber-400/5 p-3">
            <input type="checkbox" checked={covered} onChange={(e) => setCovered(e.target.checked)} className="mt-0.5 accent-amber-400" data-testid="insider-covered-checkbox" />
            <span>I'll cover the experience for the visiting team — e.g., a food tasting covered by the venue, attraction tickets covered by the business. In-kind only, never money.</span>
          </label>
          <button onClick={send} disabled={busy} className="w-full bg-amber-400 hover:bg-amber-300 text-black font-bold py-2.5 rounded-lg text-sm disabled:opacity-40" data-testid="insider-send">Send invitation</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReplyBox({ review, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const send = async () => {
    try { await api.post(`/reviews/${review.id}/reply`, { text }); toast.success("Reply posted"); setOpen(false); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="text-xs text-rose-400 flex items-center gap-1 mt-2" data-testid={`review-reply-btn-${review.id}`}><Reply size={12} /> Reply</button>;
  return (
    <div className="mt-2 flex gap-2">
      <input className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="Write a public reply..." value={text} onChange={(e) => setText(e.target.value)} data-testid={`review-reply-input-${review.id}`} />
      <button onClick={send} disabled={!text.trim()} className="text-xs font-bold bg-rose-500 px-4 rounded-lg disabled:opacity-40" data-testid={`review-reply-send-${review.id}`}>Post</button>
    </div>
  );
}


// VIP Insider Review body — collapses when content exceeds ~320px (about 8 lines of prose).
// Uses scrollHeight measurement to only show the toggle when actually clipped.
function InsiderBody({ insider }) {
  const ref = useRef(null);
  const [needsToggle, setNeedsToggle] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const MAX = 320;
  useEffect(() => {
    if (!ref.current) return;
    setNeedsToggle(ref.current.scrollHeight > MAX + 20);
  }, [insider?.body_html, insider?.body]);
  const clipped = needsToggle && !expanded;
  return (
    <div className="mt-1" data-testid="insider-body-wrap">
      <div
        ref={ref}
        style={{ maxHeight: clipped ? MAX : "none" }}
        className={`relative overflow-hidden transition-[max-height] duration-300 ${clipped ? "insider-clipped" : ""}`}
        data-testid="insider-body-inner"
      >
        {insider.body_html ? (
          <div className="rich-content text-zinc-200 leading-relaxed" data-testid="insider-rich-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(insider.body_html) }} />
        ) : (
          <PipeText text={insider.body} className="text-zinc-200 leading-relaxed" />
        )}
        {clipped && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#1a1610] via-[#1a1610]/85 to-transparent" aria-hidden="true" />
        )}
      </div>
      {needsToggle && (
        <button
          onClick={() => setExpanded((x) => !x)}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-amber-300 hover:text-amber-200 border border-amber-400/40 hover:border-amber-400/70 rounded-full px-4 py-1.5 transition-colors"
          data-testid="insider-read-more"
          aria-expanded={expanded}
        >
          {expanded ? "Show less" : "Read more"}
          <ChevronDown size={13} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
}


export function ReviewsSection({ entityType, entityId, ownerUserId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [write, setWrite] = useState(false);
  const [invite, setInvite] = useState(false);
  const load = useCallback(() => {
    api.get(`/reviews?entity_type=${entityType}&entity_id=${entityId}`).then((r) => setData(r.data)).catch(() => {});
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const isOwner = user && (user.role === "admin" || user.id === ownerUserId);
  const canWrite = user && !isOwner;
  const { items, insider, avg, count, my_review } = data;

  return (
    <section className="mt-12" data-testid="reviews-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <p className="overline mb-1">What people say</p>
          <h2 className="font-display font-bold text-2xl flex items-center gap-3">
            Reviews
            {avg != null && <span className="flex items-center gap-1.5 text-base font-normal text-zinc-300"><Stars value={avg} testid="reviews-avg-stars" /> {avg} <span className="text-zinc-500">({count})</span></span>}
          </h2>
        </div>
        <div className="flex gap-2">
          {isOwner && (
            <button onClick={() => setInvite(true)} className="text-sm font-bold border border-amber-400/50 text-amber-300 hover:bg-amber-400/10 px-4 py-2 rounded-full flex items-center gap-1.5" data-testid="invite-insider-btn"><Crown size={14} /> Invite an Insider Review</button>
          )}
          {canWrite && (
            <button onClick={() => setWrite(true)} className="text-sm font-bold bg-rose-500 hover:bg-rose-600 px-4 py-2 rounded-full flex items-center gap-1.5" data-testid="write-review-btn"><MessageSquare size={14} /> {my_review ? "Edit your review" : "Write a review"}</button>
          )}
        </div>
      </div>

      {insider && (
        <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/10 to-transparent p-6 mb-6" data-testid="insider-review">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider bg-amber-400 text-black px-2.5 py-1 rounded-full"><Crown size={12} /> The VIP Insider Review</span>
            <Stars value={insider.rating} />
          </div>
          <p className="text-xs text-amber-200/80 italic mb-3">We visit. We experience. We reveal what makes it VIP.</p>
          {insider.title && <h3 className="font-display font-bold text-lg">{insider.title}</h3>}
          <InsiderBody insider={insider} />
          <p className="text-xs text-zinc-500 mt-3">— The Gulf Coast VIP Team · {fmtD(insider.created_at)}</p>
        </div>
      )}

      {my_review && my_review.status !== "approved" && (
        <p className="text-xs text-amber-300 mb-4" data-testid="review-pending-note">Your review is {my_review.status === "rejected" ? "not approved" : "pending approval"}.</p>
      )}

      <div className="space-y-4">
        {items.map((r) => (
          <div key={r.id} className="bg-[#111] border border-white/10 rounded-xl p-5" data-testid={`review-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Stars value={r.rating} />
              {r.title && <span className="font-bold">{r.title}</span>}
            </div>
            <PipeText text={r.body} className="text-zinc-300 mt-2 text-sm leading-relaxed" />
            <p className="text-xs text-zinc-500 mt-2">{r.user_name} · {fmtD(r.created_at)}</p>
            {r.owner_reply && (
              <div className="mt-3 ml-4 border-l-2 border-rose-500/40 pl-3">
                <p className="text-xs font-bold text-rose-300">Response from {r.owner_reply.by}</p>
                <PipeText text={r.owner_reply.text} className="text-sm text-zinc-400 mt-0.5" />
              </div>
            )}
            {isOwner && !r.owner_reply && <ReplyBox review={r} onSaved={load} />}
          </div>
        ))}
      </div>
      {items.length === 0 && !insider && (
        <p className="text-zinc-500 text-sm py-6" data-testid="reviews-empty">No reviews yet{canWrite ? " — be the first to share your experience." : "."}</p>
      )}
      {!user && <p className="text-xs text-zinc-500 mt-4">Log in to write a review.</p>}

      {write && <ReviewDialog entityType={entityType} entityId={entityId} existing={my_review} onClose={() => setWrite(false)} onSaved={load} />}
      {invite && <InviteInsiderDialog entityType={entityType} entityId={entityId} onClose={() => setInvite(false)} />}
    </section>
  );
}
